import {
  isMemgraphSchemaCypherQuery,
  NODE_NAME_PROPERTIES,
} from '@/constants/graph.constants';
import type { AdjacencyGroup } from '@/types/exploration.types';
import type {
  QueryGraphCountResult,
  QueryGraphResponse,
  QueryGraphResult,
} from '@/interfaces/graphQuery.interfaces';
import neo4j, {
  isDate,
  isDateTime,
  isDuration,
  isInt,
  isLocalDateTime,
  isLocalTime,
  isNode,
  isPath,
  isPathSegment,
  isPoint,
  isRelationship,
  isTime,
  isUnboundRelationship,
} from 'neo4j-driver';
import {
  getRandomColor,
  loadProjectColorMapping,
  saveProjectColorMapping,
} from './graphStyles.helpers';

// Helper to detect and format Neo4j special types (temporal, spatial, etc.)
/**
 * Formats a value the driver returns as one of Neo4j's own types — Integer,
 * Date, Point, Node, Path and friends — or `null` when it is not one of them.
 *
 * Split out of `formatNeo4jSpecialTypes`, which ends in a catch-all that
 * `JSON.stringify`s any remaining object. That catch-all means the outer
 * function never returns `null` for an object, so a caller could not tell "this
 * is a Neo4j Date, here is the date" from "this is an ordinary map and here is
 * its JSON" — and every caller wanting to render the second kind compactly was
 * silently getting the JSON instead. Callers that want the old behaviour keep
 * calling `formatNeo4jSpecialTypes`; callers that want to handle plain data
 * themselves call this.
 */
export const formatKnownNeo4jType = (value: any): string | null => {
  if (isInt?.(value)) {
    // Neo4j Integer: return the low value for display
    return String(value.low);
  }
  if (isDate?.(value)) {
    return value.toStandardDate().toISOString().slice(0, 10); // YYYY-MM-DD
  }
  if (isDateTime?.(value) || isLocalDateTime?.(value)) {
    return value.toStandardDate().toISOString(); // Full ISO string
  }
  if (isTime?.(value) || isLocalTime?.(value)) {
    // Only time part
    return value.toString();
  }
  if (isPoint?.(value)) {
    if (value.x !== undefined && value.y !== undefined) {
      if (value.z !== undefined) {
        return `Point(${value.x}, ${value.y}, ${value.z})`;
      } else {
        return `Point(${value.x}, ${value.y})`;
      }
    }
    return value.toString();
  }
  if (isDuration?.(value)) {
    // Neo4j Duration: convert to ISO 8601 duration string
    // value.toString() returns ISO 8601
    return value.toString();
  }
  if (isNode?.(value)) {
    // Node: show identity and labels
    return `Node(id: ${value.identity}, labels: ${value.labels?.join(', ')})`;
  }
  if (isRelationship?.(value) || isUnboundRelationship?.(value)) {
    // Relationship: show identity and type
    return `Relationship(id: ${value.identity}, type: ${value.type})`;
  }
  if (isPath?.(value)) {
    // Path: show start/end and length
    const start = value.start?.identity;
    const end = value.end?.identity;
    const length = value.segments?.length || 0;
    return `Path(${start} -> ${end}, length: ${length})`;
  }
  if (isPathSegment?.(value)) {
    // PathSegment: show start/end node identities and relationship type
    const start = value.start?.identity;
    const end = value.end?.identity;
    const relType = value.relationship?.type;
    return `PathSegment(${start} -[${relType}]-> ${end})`;
  }
  // A Neo4j Integer that arrived as JSON rather than as a driver instance:
  // `isInt()` only recognises the class, so a serialized `{low, high}` fell
  // through to the generic object handling and rendered as its own internals.
  //
  // Rebuilt through the driver rather than read by hand. `low` and `high` are
  // *signed* 32-bit words, so `{low: -1, high: 0}` is 4294967295 and not -1 —
  // printing `low` on its own turned a large unsigned value into a small
  // negative one, which is a wrong number rather than an ugly one. `toString`
  // also carries the full 64-bit range, so a value past `MAX_SAFE_INTEGER`
  // stays exact instead of being rounded by a cast to `number`.
  if (
    value &&
    typeof value === 'object' &&
    typeof value.low === 'number' &&
    typeof value.high === 'number' &&
    Object.keys(value).length === 2
  ) {
    return new neo4j.types.Integer(value.low, value.high).toString();
  }
  // Detect Neo4j date/datetime-like plain objects (not class instances)
  // Handles both Neo4j Integer objects (with .low property) and plain numbers
  if (
    value &&
    typeof value === 'object' &&
    value.year !== undefined &&
    value.month !== undefined &&
    value.day !== undefined
  ) {
    // Extract values - handle both Neo4j Integer objects and plain numbers
    const getValue = (val: any): number => {
      if (val && typeof val === 'object' && val.low !== undefined) {
        return val.low; // Neo4j Integer object
      }
      return typeof val === 'number' ? val : Number(val) || 0; // Plain number
    };

    const year = getValue(value.year);
    const month = getValue(value.month);
    const day = getValue(value.day);

    if ('hour' in value && 'minute' in value && 'second' in value) {
      const hour = getValue(value.hour);
      const minute = getValue(value.minute);
      const second = getValue(value.second);
      const nanosecond = getValue(value.nanosecond ?? 0);
      const timeZoneOffsetSeconds = getValue(value.timeZoneOffsetSeconds ?? 0);

      try {
        const dt = new neo4j.types.DateTime(
          year,
          month,
          day,
          hour,
          minute,
          second,
          nanosecond,
          timeZoneOffsetSeconds,
        );
        return dt.toStandardDate().toISOString();
      } catch {
        return `${year}-${String(month).padStart(2, '0')}-${String(
          day,
        ).padStart(2, '0')} ${String(hour).padStart(2, '0')}:${String(
          minute,
        ).padStart(2, '0')}:${String(second).padStart(2, '0')}`;
      }
    } else {
      try {
        const d = new neo4j.types.Date(year, month, day);
        return d.toStandardDate().toISOString().slice(0, 10);
      } catch {
        return `${year}-${String(month).padStart(2, '0')}-${String(
          day,
        ).padStart(2, '0')}`;
      }
    }
  }
  // Detect Neo4j Point-like plain objects (not class instances)
  if (
    value &&
    typeof value === 'object' &&
    value.srid !== undefined &&
    (value.x !== undefined || value.coordinates)
  ) {
    const x =
      value.x && typeof value.x === 'object' && value.x.low !== undefined
        ? value.x.low
        : value.x;
    const y =
      value.y && typeof value.y === 'object' && value.y.low !== undefined
        ? value.y.low
        : value.y;
    const z =
      value.z && typeof value.z === 'object' && value.z.low !== undefined
        ? value.z.low
        : value.z;
    if (z !== undefined) {
      return `Point(${x}, ${y}, ${z})`;
    } else {
      return `Point(${x}, ${y})`;
    }
  }
  return null;
};

/**
 * As `formatKnownNeo4jType`, but falling back to JSON for any other object.
 *
 * Kept exactly as it was so every existing caller is unaffected.
 */
export const formatNeo4jSpecialTypes = (value: any): string | null => {
  const known = formatKnownNeo4jType(value);
  if (known !== null) return known;
  // Fallback for unknown objects
  if (typeof value === 'object' && value !== null) {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return null;
};

/**
 * Generic base labels the backend attaches to every node (e.g. `_Entity`).
 * These are not meaningful for display, so the graph caption/color should
 * prefer the specific label (e.g. `Item`, `ProppantUsage`) instead.
 */
const GENERIC_BASE_LABELS = new Set(['_Entity']);

/**
 * Picks the most meaningful label for display, skipping generic base labels
 * like `_Entity`. Falls back to the first label, then `'Unknown'`.
 */
export const getDisplayNodeLabel = (labels?: unknown): string => {
  if (!Array.isArray(labels) || labels.length === 0) return 'Unknown';
  const meaningful = labels.find(
    (l) => typeof l === 'string' && l !== '' && !GENERIC_BASE_LABELS.has(l),
  );
  return String(meaningful ?? labels[0]);
};

export const formatNodesAndRelationshipsFromResult = async (
  result: any,
  projectId: string,
) => {
  // Load color mapping from IndexedDB
  const originalMapping = await loadProjectColorMapping(projectId);
  const projectColorMapping = { ...originalMapping }; // Create a working copy
  const usedColors = new Set<string>();

  // Create a map of numeric id -> element_id for nodes (to use in relationships)
  const nodeIdToElementIdMap = new Map<number, string>();
  result.nodes.forEach((node: any) => {
    if (node.id !== undefined && node.element_id) {
      nodeIdToElementIdMap.set(node.id, node.element_id);
    }
  });

  // Format nodes - new API structure has labels and properties directly on the node
  // Use element_id as the id
  const nodes = result.nodes.map((node: any) => {
    const nodeLabel = getDisplayNodeLabel(node.labels);

    // Get or assign color for this node label
    let nodeColor = projectColorMapping[nodeLabel];
    if (!nodeColor) {
      nodeColor = getRandomColor(usedColors);
      usedColors.add(nodeColor);
      projectColorMapping[nodeLabel] = nodeColor;
    }

    // Generate caption from properties (same logic as before)
    const caption =
      node.properties?.name ||
      node.properties?.product_name ||
      node.properties?.contract_terms ||
      nodeLabel;

    return {
      id: node.element_id, // Use element_id as id
      element_id: node.element_id,
      caption,
      properties: node.properties || {},
      labels: node.labels || [],
      color: nodeColor,
    };
  });

  // Format relationships - new API structure has type and properties directly on the relationship
  // Map start_node.id and end_node.id to their corresponding element_ids
  const relationships = result.relationships.map((rel: any) => {
    const startNodeId = rel.start_node?.element_id ?? rel.start_node;
    const endNodeId = rel.end_node?.element_id ?? rel.end_node;

    // Look up element_id from the map
    const fromElementId =
      startNodeId !== undefined
        ? (nodeIdToElementIdMap.get(startNodeId) ?? startNodeId)
        : startNodeId;
    const toElementId =
      endNodeId !== undefined
        ? (nodeIdToElementIdMap.get(endNodeId) ?? endNodeId)
        : endNodeId;

    return {
      id: rel.element_id, // Use element_id as id
      element_id: rel.element_id,
      from: fromElementId,
      to: toElementId,
      caption: rel.type,
      properties: rel.properties || {},
      type: rel.type,
    };
  });

  // Save updated color mapping if any new colors were added
  // Check if we added any new colors by comparing current mapping with original
  const hasNewColors = Object.keys(projectColorMapping).some(
    (key) =>
      !originalMapping[key] ||
      originalMapping[key] !== projectColorMapping[key],
  );

  if (hasNewColors) {
    try {
      await saveProjectColorMapping(projectId, projectColorMapping);
    } catch (error) {
      console.error('Error saving project color mapping:', error);
    }
  }

  return { nodes, relationships };
};

/**
 * Converts a `SHOW SCHEMA INFO` response object into the `{ nodes, relationships }`
 * shape consumed by `formatSchemaQueryResult`.
 *
 * `SHOW SCHEMA INFO` returns:
 *   `nodes`: array of `{ labels, count, properties }`
 *   `edges`: array of `{ type, start_node_labels, end_node_labels, count, properties }`
 *
 * Node ids are synthetic zero-based integers so relationship `start`/`end`
 * indices can reference them by position.
 */
export const buildSchemaGraphFromShowSchemaInfo = (
  schemaInfo: Record<string, unknown>,
): QueryGraphResult => {
  const rawNodes = Array.isArray(schemaInfo.nodes) ? schemaInfo.nodes : [];
  const rawEdges = Array.isArray(schemaInfo.edges) ? schemaInfo.edges : [];

  const labelSetToId = new Map<string, number>();
  const labelToId = new Map<string, number>();
  const toLabels = (value: unknown): string[] =>
    Array.isArray(value) ? value.map(String).filter(Boolean) : [];
  const labelSetKey = (labels: string[]): string =>
    [...labels].sort().join('\u0000');
  const displayLabel = (labels: string[], fallback: string): string =>
    labels.find((label) => !label.startsWith('_')) ?? labels[0] ?? fallback;

  const nodes: QueryGraphResult['nodes'] = rawNodes.map(
    (n: any, idx: number) => {
      const rawLabels = toLabels(n.labels);
      const label = displayLabel(rawLabels, `node_${idx}`);
      const labels = [label, ...rawLabels.filter((item) => item !== label)];

      labelSetToId.set(labelSetKey(rawLabels), idx);
      for (const item of rawLabels) {
        if (!labelToId.has(item)) labelToId.set(item, idx);
      }
      if (!labelToId.has(label)) labelToId.set(label, idx);

      return {
        id: idx,
        // Put the meaningful label first so formatSchemaQueryResult uses it as
        // the caption. Memgraph commonly returns generic labels such as
        // `_Entity` before the business label; leaving that order intact makes
        // the underscore filter remove the entire schema entity.
        labels,
        properties: {},
        type: 'node',
      };
    },
  );

  const relationships: QueryGraphResult['relationships'] = [];
  let nextRelId = 0;

  for (const edge of rawEdges) {
    const startLabels = toLabels(edge.start_node_labels);
    const endLabels = toLabels(edge.end_node_labels);
    const startLabel = displayLabel(startLabels, '');
    const endLabel = displayLabel(endLabels, '');

    if (!startLabel || !endLabel || !edge.type) continue;

    // Prefer the exact label-set match because multiple schema entries can
    // share a generic label (for example `_Entity`). Fall back to the selected
    // meaningful label for older/partial SHOW SCHEMA INFO payloads.
    const fromId =
      labelSetToId.get(labelSetKey(startLabels)) ?? labelToId.get(startLabel);
    const toId =
      labelSetToId.get(labelSetKey(endLabels)) ?? labelToId.get(endLabel);

    // Skip edges whose endpoint labels are not present in the nodes list.
    if (fromId === undefined || toId === undefined) continue;

    relationships.push({
      id: nextRelId++,
      start: fromId,
      end: toId,
      label: String(edge.type),
      type: 'relationship',
      properties: {},
    });
  }

  return { nodes, relationships };
};

/**
 * Extracts the `SHOW SCHEMA INFO` object from the API response.
 *
 * Handles the actual backend envelope:
 *   `{ results: [{ schema: "<json-string>" }], ... }`
 * where `schema` is a JSON-serialized string containing `nodes` and `edges`.
 * Also accepts the object directly or array-wrapped for forward compatibility.
 */
export const extractShowSchemaInfoObject = (
  apiResponse: QueryGraphResponse,
): Record<string, unknown> | null => {
  const { results } = apiResponse;

  const looksLikeSchemaInfo = (v: unknown): v is Record<string, unknown> =>
    v !== null &&
    typeof v === 'object' &&
    !Array.isArray(v) &&
    ('nodes' in v || 'edges' in v);

  // Parse a JSON-string value if present (actual backend shape for SHOW SCHEMA INFO).
  const tryParseSchemaString = (v: unknown): Record<string, unknown> | null => {
    if (typeof v !== 'string') return null;
    try {
      const parsed = JSON.parse(v);
      return looksLikeSchemaInfo(parsed) ? parsed : null;
    } catch {
      return null;
    }
  };

  if (looksLikeSchemaInfo(results)) return results;

  if (Array.isArray(results) && results.length > 0) {
    const first = results[0] as any;
    // Primary backend shape: results[0].schema is a JSON string.
    if (typeof first?.schema === 'string') {
      const parsed = tryParseSchemaString(first.schema);
      if (parsed) return parsed;
    }
    if (looksLikeSchemaInfo(first)) return first as Record<string, unknown>;
  }

  if (
    results &&
    typeof results === 'object' &&
    'records' in results &&
    Array.isArray((results as any).records) &&
    (results as any).records.length > 0
  ) {
    const first = (results as any).records[0];
    const parsed = tryParseSchemaString(first?.schema);
    if (parsed) return parsed;
    if (looksLikeSchemaInfo(first)) return first as Record<string, unknown>;
  }

  return null;
};

/**
 * What a stored property can be summarized as. Engines disagree on type names
 * (Memgraph says `Integer`/`ZonedDateTime`, Neo4j says `Long`/`DateTime`), so
 * callers get this normalized shape instead of raw engine spellings.
 */
export type SchemaPropertyKind =
  'number' | 'date' | 'boolean' | 'text' | 'other';

export type PropertyKindsByLabel = Record<
  string,
  Record<string, SchemaPropertyKind>
>;

const PROPERTY_KIND_BY_ENGINE_TYPE: Record<string, SchemaPropertyKind> = {
  integer: 'number',
  int: 'number',
  long: 'number',
  float: 'number',
  double: 'number',
  number: 'number',
  date: 'date',
  datetime: 'date',
  localdatetime: 'date',
  zoneddatetime: 'date',
  localtime: 'date',
  time: 'date',
  boolean: 'boolean',
  bool: 'boolean',
  string: 'text',
};

/**
 * Collapse an engine type name to a kind. Unknown names (lists, maps, durations,
 * point types, anything a future engine adds) fall back to `other` so callers
 * never treat something as summable on a guess.
 */
export const normalizeSchemaPropertyKind = (
  rawType: unknown,
): SchemaPropertyKind => {
  if (typeof rawType !== 'string') return 'other';
  return PROPERTY_KIND_BY_ENGINE_TYPE[rawType.trim().toLowerCase()] ?? 'other';
};

/**
 * Reduce the type list a schema reports for one property to a single kind.
 * A property that holds more than one kind across the store (say numbers in
 * some rows and text in others) is `other`: it is not honestly summable.
 */
const reducePropertyKinds = (rawTypes: unknown[]): SchemaPropertyKind => {
  const kinds = new Set(rawTypes.map(normalizeSchemaPropertyKind));
  if (kinds.size !== 1) return 'other';
  return [...kinds][0];
};

/** Business labels for a `SHOW SCHEMA INFO` node entry, generic ones dropped. */
const resolveSchemaNodeLabels = (node: any): string[] => {
  const rawLabels = Array.isArray(node?.labels)
    ? node.labels.map(String).filter(Boolean)
    : [];
  const businessLabels = rawLabels.filter(
    (label: string) => !label.startsWith('_'),
  );
  return businessLabels.length > 0 ? businessLabels : rawLabels;
};

/**
 * Extracts per-label property *kinds* from a Memgraph `SHOW SCHEMA INFO`
 * object, whose property descriptors carry `types: [{ count, type }]`
 * alongside the key. Companion to `extractPropertiesByLabelFromShowSchemaInfo`,
 * which returns the names alone.
 *
 * When two labels in the same store disagree about a property's kind they are
 * recorded independently, since each label is looked up on its own.
 */
export const extractPropertyKindsByLabelFromShowSchemaInfo = (
  schemaInfo: Record<string, unknown>,
): PropertyKindsByLabel => {
  const rawNodes = Array.isArray(schemaInfo.nodes) ? schemaInfo.nodes : [];
  const out: PropertyKindsByLabel = {};

  for (const node of rawNodes as any[]) {
    const labels = resolveSchemaNodeLabels(node);
    if (labels.length === 0) continue;

    const props = Array.isArray(node?.properties) ? node.properties : [];
    for (const p of props) {
      if (!p || typeof p !== 'object') continue;
      const name =
        (p as any).key ??
        (p as any).property ??
        (p as any).name ??
        (p as any).propertyName;
      if (typeof name !== 'string' || name.length === 0) continue;

      const rawTypes = Array.isArray((p as any).types)
        ? (p as any).types.map((t: any) =>
            typeof t === 'string' ? t : t?.type,
          )
        : [(p as any).type];
      const kind = reducePropertyKinds(rawTypes);

      for (const label of labels) {
        const forLabel = (out[label] ??= {});
        // A label can appear in several node entries (different label sets).
        // Disagreement across those entries means the same caveat as
        // disagreement within one: do not claim a summable kind.
        forLabel[name] =
          forLabel[name] === undefined || forLabel[name] === kind
            ? kind
            : 'other';
      }
    }
  }

  return out;
};

/**
 * Builds per-label property kinds from Neo4j `db.schema.nodeTypeProperties()`
 * rows, which report `propertyTypes` as a list of type names per property.
 */
export const buildPropertyKindsByLabelFromNodeTypeRows = (
  rows: Record<string, unknown>[],
): PropertyKindsByLabel => {
  const out: PropertyKindsByLabel = {};

  for (const row of rows) {
    const labelsVal = (row as any).nodeLabels ?? (row as any).nodelabels;
    const labels = Array.isArray(labelsVal)
      ? labelsVal.map((l) => String(l))
      : typeof labelsVal === 'string'
        ? [labelsVal]
        : [];

    const propName = (row as any).propertyName ?? (row as any).propertyname;
    if (typeof propName !== 'string' || propName.length === 0) continue;

    const typesVal = (row as any).propertyTypes ?? (row as any).propertytypes;
    const rawTypes = Array.isArray(typesVal) ? typesVal : [typesVal];
    const kind = reducePropertyKinds(rawTypes);

    for (const label of labels) {
      if (!label) continue;
      const forLabel = (out[label] ??= {});
      forLabel[propName] =
        forLabel[propName] === undefined || forLabel[propName] === kind
          ? kind
          : 'other';
    }
  }

  return out;
};

/**
 * Extracts per-label property keys from a Memgraph `SHOW SCHEMA INFO` object.
 *
 * `SHOW SCHEMA INFO` node entries carry `{ labels, count, properties }` where
 * `properties` is an array of descriptors. Memgraph names the key field `key`;
 * we also accept `property` / `name` / `propertyName` and bare strings for
 * forward/backward compatibility. Properties are attributed to every business
 * label in the entry; underscore-prefixed generic labels are excluded when a
 * business label is present.
 */
export const extractPropertiesByLabelFromShowSchemaInfo = (
  schemaInfo: Record<string, unknown>,
): Record<string, string[]> => {
  const rawNodes = Array.isArray(schemaInfo.nodes) ? schemaInfo.nodes : [];
  const byLabel: Record<string, Set<string>> = {};

  for (const node of rawNodes as any[]) {
    const rawLabels = Array.isArray(node?.labels)
      ? node.labels.map(String).filter(Boolean)
      : [];
    const businessLabels = rawLabels.filter(
      (label: string) => !label.startsWith('_'),
    );
    const labels = businessLabels.length > 0 ? businessLabels : rawLabels;
    if (labels.length === 0) continue;

    const props = Array.isArray(node?.properties) ? node.properties : [];
    const propertyNames: string[] = [];
    for (const p of props) {
      let name: string | null = null;
      if (typeof p === 'string') {
        name = p;
      } else if (p && typeof p === 'object') {
        const candidate =
          (p as any).key ??
          (p as any).property ??
          (p as any).name ??
          (p as any).propertyName;
        if (typeof candidate === 'string') name = candidate;
      }
      if (name && name.length > 0) propertyNames.push(name);
    }
    for (const label of labels) {
      const keys = (byLabel[label] ??= new Set<string>());
      for (const name of propertyNames) keys.add(name);
    }
  }

  const out: Record<string, string[]> = {};
  for (const [label, set] of Object.entries(byLabel)) {
    out[label] = [...set].sort((a, b) => a.localeCompare(b));
  }
  return out;
};

/**
 * Builds `Record<label, string[]>` property metadata from Neo4j
 * `db.schema.nodeTypeProperties()` rows. Each row carries a `nodeLabels` list
 * and a single `propertyName`; the property is attributed to every label in
 * the row so callers keyed on a specific display label find it.
 */
export const buildPropertiesByLabelFromNodeTypeRows = (
  rows: Record<string, unknown>[],
): Record<string, string[]> => {
  const byLabel: Record<string, Set<string>> = {};

  for (const row of rows) {
    const labelsVal = (row as any).nodeLabels ?? (row as any).nodelabels;
    const labels = Array.isArray(labelsVal)
      ? labelsVal.map((l) => String(l))
      : typeof labelsVal === 'string'
        ? [labelsVal]
        : [];

    const propName = (row as any).propertyName ?? (row as any).propertyname;
    if (typeof propName !== 'string' || propName.length === 0) continue;

    for (const label of labels) {
      if (!label) continue;
      (byLabel[label] ??= new Set<string>()).add(propName);
    }
  }

  const out: Record<string, string[]> = {};
  for (const [label, set] of Object.entries(byLabel)) {
    out[label] = [...set].sort((a, b) => a.localeCompare(b));
  }
  return out;
};

/** Resolve API payload into schema graph data for Neo4j or Memgraph queries. */
export const resolveSchemaQueryResult = (
  apiResponse: QueryGraphResponse,
  query: string,
): QueryGraphResult => {
  if (isMemgraphSchemaCypherQuery(query)) {
    const schemaInfo = extractShowSchemaInfoObject(apiResponse);
    if (!schemaInfo) {
      throw new Error('Invalid Memgraph schema response');
    }
    return buildSchemaGraphFromShowSchemaInfo(schemaInfo);
  }

  const raw = apiResponse.results as QueryGraphResult | QueryGraphResult[];
  const result = Array.isArray(raw) ? raw[0] : raw;
  if (!result?.nodes) {
    throw new Error('Invalid schema response: missing nodes');
  }
  return result;
};

export const formatSchemaQueryResult = async (
  result: any,
  projectId: string,
) => {
  // Load color mapping from IndexedDB
  const originalMapping = await loadProjectColorMapping(projectId);
  const projectColorMapping = { ...originalMapping }; // Create a working copy
  const usedColors = new Set<string>();

  /** Neo4j /graph/query schema nodes may use `labels` + `element_id` without `name` (see db.schema.visualization). */
  const schemaNodeLabel = (node: any): string =>
    (typeof node?.name === 'string' && node.name) ||
    (Array.isArray(node?.labels) && node.labels.length > 0
      ? String(node.labels[0])
      : '') ||
    'Unknown';

  const schemaNodeId = (node: any, label: string): string => {
    if (node?.element_id != null && String(node.element_id) !== '') {
      return String(node.element_id);
    }
    // Memgraph `SHOW SCHEMA INFO` nodes use synthetic numeric ids (no element_id).
    if (node?.id !== undefined && node?.id !== null && String(node.id) !== '') {
      return String(node.id);
    }
    return label;
  };

  // Format nodes - schema query nodes have name, indexes, and constraints
  const nodes = result.nodes.map((node: any) => {
    const nodeName = schemaNodeLabel(node);
    const nodeId = schemaNodeId(node, nodeName);

    // Get or assign color for this node label
    let nodeColor = projectColorMapping[nodeName];
    if (!nodeColor) {
      nodeColor = getRandomColor(usedColors);
      usedColors.add(nodeColor);
      projectColorMapping[nodeName] = nodeColor;
    }

    const labels =
      Array.isArray(node.labels) && node.labels.length > 0
        ? node.labels.map((l: any) => String(l))
        : [nodeName];

    const rawProps =
      node.properties &&
      typeof node.properties === 'object' &&
      !Array.isArray(node.properties)
        ? { ...(node.properties as Record<string, unknown>) }
        : {};

    return {
      id: nodeId,
      caption: nodeName,
      properties: {
        ...rawProps,
        indexes:
          node.indexes ?? (rawProps as { indexes?: unknown }).indexes ?? [],
        constraints:
          node.constraints ??
          (rawProps as { constraints?: unknown }).constraints ??
          [],
      },
      labels,
      color: nodeColor,
    };
  });

  const nodeNumericIdMap = new Map<number, string>();
  result.nodes.forEach((node: any) => {
    if (node?.id !== undefined) {
      const nid = Number(node.id);
      if (!Number.isNaN(nid)) {
        nodeNumericIdMap.set(nid, schemaNodeId(node, schemaNodeLabel(node)));
      }
    }
  });

  const resolveSchemaEndpoint = (ref: any): string | null => {
    if (ref == null) return null;
    if (typeof ref === 'string') return ref;
    if (typeof ref === 'number') {
      return nodeNumericIdMap.get(ref) ?? String(ref);
    }
    if (typeof ref === 'object') {
      if (ref.element_id != null && String(ref.element_id) !== '') {
        return String(ref.element_id);
      }
      if (typeof ref.id === 'number') {
        const mapped = nodeNumericIdMap.get(ref.id);
        if (mapped != null) return mapped;
      }
      const lbl = schemaNodeLabel(ref);
      return schemaNodeId(ref, lbl);
    }
    return null;
  };

  const schemaRelationshipType = (rel: any): string => {
    if (typeof rel?.label === 'string' && rel.label !== '') {
      return rel.label;
    }
    if (typeof rel?.type === 'string' && rel.type !== 'relationship') {
      return rel.type;
    }
    if (typeof rel?.caption === 'string' && rel.caption !== '') {
      return rel.caption;
    }
    return 'UNKNOWN';
  };

  // Schema relationships: tuple [start, type, end] from Neo4j Browser-style, or graph API objects
  const relationships = result.relationships
    .map((rel: any, index: number) => {
      if (Array.isArray(rel) && rel.length >= 3) {
        const startNode = rel[0];
        const relType = rel[1];
        const endNode = rel[2];

        const fromLabel = schemaNodeLabel(startNode);
        const toLabel = schemaNodeLabel(endNode);
        const fromId = schemaNodeId(startNode, fromLabel);
        const toId = schemaNodeId(endNode, toLabel);
        const relationshipType =
          typeof relType === 'string' ? relType : 'UNKNOWN';

        return {
          id: `${fromId}-${relationshipType}-${toId}-${index}`,
          from: fromId,
          to: toId,
          caption: relationshipType,
          properties: {},
          type: relationshipType,
        };
      }

      if (rel && typeof rel === 'object' && !Array.isArray(rel)) {
        let fromId: string | null = null;
        let toId: string | null = null;

        if (rel.start_node != null && rel.end_node != null) {
          fromId = resolveSchemaEndpoint(rel.start_node);
          toId = resolveSchemaEndpoint(rel.end_node);
        } else if (rel.from != null && rel.to != null) {
          fromId = resolveSchemaEndpoint(rel.from);
          toId = resolveSchemaEndpoint(rel.to);
        } else if (rel.start != null && rel.end != null) {
          fromId = resolveSchemaEndpoint(rel.start);
          toId = resolveSchemaEndpoint(rel.end);
        }

        if (fromId && toId) {
          const relationshipType = schemaRelationshipType(rel);

          return {
            id: String(
              rel.element_id ??
                rel.id ??
                `${fromId}-${relationshipType}-${toId}-${index}`,
            ),
            from: fromId,
            to: toId,
            caption: relationshipType,
            properties: rel.properties || {},
            type: relationshipType,
          };
        }
      }

      return null;
    })
    .filter((rel: any) => rel !== null);

  // Save updated color mapping if any new colors were added
  const hasNewColors = Object.keys(projectColorMapping).some(
    (key) =>
      !originalMapping[key] ||
      originalMapping[key] !== projectColorMapping[key],
  );

  if (hasNewColors) {
    try {
      await saveProjectColorMapping(projectId, projectColorMapping);
    } catch (error) {
      console.error('Error saving project color mapping:', error);
    }
  }

  return { nodes, relationships };
};

export const logGraphQuery = (query: string, params: any) => {
  console.log('Executing query:', query, 'with params:', params);
};

export const extractNodeTypeCounts = (neo4jResult: QueryGraphCountResult[]) => {
  const nodeTypeCounts: { [key: string]: number } = {};

  neo4jResult.forEach((record) => {
    const nodeType = record.nodeType;
    nodeTypeCounts[nodeType] = record.count;
  });

  return nodeTypeCounts;
};

/**
 * Filters grouped adjacency counts, subtracting already-connected nodes per
 * target label while preserving the rel-type breakdown.
 */
const adjacencyBucketKey = (relType: string, direction: string): string =>
  `${relType} ${direction}`;

/**
 * Counts already-drawn neighbours of `currentNodeId`, bucketed by
 * `(targetLabel, relType, direction)` — the SAME grain the backend's
 * `adjacency_counts` rows use, so the two can be subtracted exactly.
 *
 * Distinct far-node ids are counted per bucket (mirroring the server's
 * `count(DISTINCT m.id)`), so two parallel edges of the same type between
 * the same pair count once, exactly as the server counts them.
 */
const countRenderedNeighboursByRelType = (
  currentNodeId: string,
  nodes: any[],
  relationships: any[],
): Map<string, Map<string, Set<string>>> => {
  const labelById = new Map<string, string>();
  nodes.forEach((node) => {
    if (!node?.id || node.id === currentNodeId) return;
    const label = getDisplayNodeLabel(node.labels);
    if (label !== 'Unknown') labelById.set(String(node.id), label);
  });

  // label -> bucket key -> distinct far-node ids already on the canvas
  const byLabel = new Map<string, Map<string, Set<string>>>();

  const record = (farId: string, relType: string, direction: string) => {
    const label = labelById.get(farId);
    // Only nodes actually RENDERED count as "already shown"; a dangling
    // relationship whose endpoint was filtered/capped away must not
    // suppress an expansion the user can still legitimately perform.
    if (!label || !relType) return;
    const buckets = byLabel.get(label) ?? new Map<string, Set<string>>();
    const key = adjacencyBucketKey(relType, direction);
    const ids = buckets.get(key) ?? new Set<string>();
    ids.add(farId);
    buckets.set(key, ids);
    byLabel.set(label, buckets);
  };

  relationships.forEach((rel) => {
    const relType = String(rel?.type ?? rel?.caption ?? '');
    const from = String(rel?.from ?? '');
    const to = String(rel?.to ?? '');
    // Direction is expressed FROM the popup node's perspective, matching
    // the server rows: an edge leaving it is 'out', one arriving 'in'.
    // A self-loop is both legs of its own adjacency.
    if (from === currentNodeId) record(to, relType, 'out');
    if (to === currentNodeId) record(from, relType, 'in');
  });

  return byLabel;
};

/**
 * Subtracts neighbours already on the canvas from the server's adjacency
 * counts, so the popup offers only what an expand would actually add.
 *
 * The subtraction is attributed EXACTLY, per `(relType, direction)`, using
 * the type carried on each rendered relationship. It previously scaled
 * every rel type by `remaining / total` and rounded — an estimate that
 * could round a rare rel type down to zero (silently removing a valid
 * expansion from the menu), round two halves back up so the subtraction
 * cancelled itself, and drift the group total away from the sum of its
 * parts. It also subtracted a distinct-NODE count from `total`, which is
 * a SUM over per-relType counts, so a neighbour reachable by two rel
 * types was double-counted on one side of the subtraction only.
 *
 * `total` keeps the server's adjacency truth (what the popup displays),
 * `remaining` is how many an expand would still add (what the click
 * does), and `presentCount` is the part already on the canvas — so the
 * UI never shows a bare decremented number that contradicts the graph,
 * the table and the database.
 */
export const filterExistingAdjacencyGroups = (
  groups: AdjacencyGroup[],
  currentNodeId: string,
  nodes: any[],
  relationships: any[],
): AdjacencyGroup[] => {
  const renderedByLabel = countRenderedNeighboursByRelType(
    currentNodeId,
    nodes,
    relationships,
  );

  return groups
    .map((group): AdjacencyGroup | null => {
      const buckets = renderedByLabel.get(group.targetLabel);

      // ANNOTATE, never mutate: `count` / `total` keep the server's
      // adjacency truth so the displayed number always reproduces a
      // Cypher count, and `remaining` carries what an expand would add.
      const relTypes = group.relTypes.map((rel) => {
        const present =
          buckets?.get(adjacencyBucketKey(rel.relType, rel.direction))?.size ??
          0;
        // Clamp at 0: the canvas can legitimately hold more of a type than
        // the server reports (e.g. nodes pulled in by a different expand),
        // and a negative remainder must not borrow from a sibling rel type
        // the way the old proportional scale did.
        return { ...rel, remaining: Math.max(0, rel.count - present) };
      });

      const remaining = relTypes.reduce((sum, rel) => sum + rel.remaining, 0);
      // Nothing left to add: the whole neighbourhood is already drawn.
      if (remaining <= 0) return null;

      const total =
        group.relTypes.length > 0
          ? group.relTypes.reduce((sum, rel) => sum + rel.count, 0)
          : group.total;

      return {
        targetLabel: group.targetLabel,
        total,
        remaining,
        presentCount: Math.max(0, total - remaining),
        relTypes,
      };
    })
    .filter((group): group is AdjacencyGroup => group !== null);
};

/**
 * Filters out relationship counts for node types that are already present in the graph.
 * This prevents showing redundant relationship options in the popup.
 */
export const filterExistingNodeTypeCounts = (
  relationshipCounts: Record<string, number>,
  currentNodeId: string,
  nodes: any[],
  relationships: any[],
): Record<string, number> => {
  // Label-grain subtraction, computed directly rather than delegating to
  // filterExistingAdjacencyGroups: this input carries counts per LABEL
  // with no rel-type breakdown, and the previous delegation synthesised a
  // fake `{ relType: <label>, direction: 'both' }` entry to fit the
  // richer signature. That fabricated type matches no real edge, so the
  // exact per-relType attribution can never resolve it.
  const labelById = new Map<string, string>();
  nodes.forEach((node) => {
    if (!node?.id || node.id === currentNodeId) return;
    const label = getDisplayNodeLabel(node.labels);
    if (label !== 'Unknown') labelById.set(String(node.id), label);
  });

  // Distinct rendered neighbours per label (a node joined by several
  // edges is one neighbour, matching the server's DISTINCT counting).
  const presentByLabel = new Map<string, Set<string>>();
  relationships.forEach((rel) => {
    const from = String(rel?.from ?? '');
    const to = String(rel?.to ?? '');
    let farId = '';
    if (from === currentNodeId) farId = to;
    else if (to === currentNodeId) farId = from;
    if (!farId) return;
    const label = labelById.get(farId);
    if (!label) return;
    const ids = presentByLabel.get(label) ?? new Set<string>();
    ids.add(farId);
    presentByLabel.set(label, ids);
  });

  const filteredCounts: Record<string, number> = {};
  for (const [label, total] of Object.entries(relationshipCounts)) {
    const remaining = total - (presentByLabel.get(label)?.size ?? 0);
    if (remaining > 0) filteredCounts[label] = remaining;
  }
  return filteredCounts;
};

// Helper function to merge nodes by ID
export const mergeNodesById = (existingNodes: any[], newNodes: any[]) => {
  const nodeMap = new Map();

  // Add existing nodes to map
  existingNodes.forEach((node) => {
    const nodeId = node.id;
    if (nodeId) {
      nodeMap.set(nodeId, node);
    }
  });

  // Add new nodes to map (will overwrite existing ones with same ID)
  newNodes.forEach((node) => {
    const nodeId = node.id;
    if (nodeId) {
      nodeMap.set(nodeId, node);
    }
  });

  return Array.from(nodeMap.values());
};

// Helper function to merge relationships by ID
export const mergeRelationshipsById = (
  existingRelationships: any[],
  newRelationships: any[],
) => {
  const relationshipMap = new Map();

  // Add existing relationships to map
  existingRelationships.forEach((rel) => {
    const relId = rel.id;
    if (relId) {
      relationshipMap.set(relId, rel);
    }
  });

  // Add new relationships to map (will overwrite existing ones with same ID)
  newRelationships.forEach((rel) => {
    const relId = rel.id;
    if (relId) {
      relationshipMap.set(relId, rel);
    }
  });

  return Array.from(relationshipMap.values());
};

export const blacklistedNodeProperties = ['process_id', 'caption'];

export const blacklistedProperties = [
  'caption',
  'id',
  'color',
  'labels',
  'properties',
];

/** Keys to exclude when flattening a raw node into sidebar properties (e.g. dashboard card drawer). */
export const nodeMetaKeysForFlatten = new Set([
  ...blacklistedProperties,
  'element_id',
  'node_id',
  'nodeId',
  'identity',
  'type',
  'selected',
]);

/**
 * Filters nodes and relationships based on node captions starting with a specific prefix.
 * Removes nodes whose captions start with the given prefix and their associated relationships.
 */
export const filterNodesByCaptionStartsWith = (
  nodes: any[],
  relationships: any[],
  prefix: string,
): [any[], any[]] => {
  // Filter out nodes with captions starting with the specified prefix
  const filteredNodes = nodes.filter(
    (node: any) => !node.caption?.startsWith(prefix),
  );

  // Get IDs of filtered nodes to filter relationships
  const filteredNodeIds = new Set(filteredNodes.map((node: any) => node.id));

  // Filter relationships to only include those where both from and to nodes are in the filtered nodes
  const filteredRelationships = relationships.filter(
    (rel: any) => filteredNodeIds.has(rel.from) && filteredNodeIds.has(rel.to),
  );

  return [filteredNodes, filteredRelationships];
};

/**
 * Apply display property mappings to nodes
 * This is used to update node captions based on stored display property preferences
 */
export const applyDisplayPropertiesToNodes = async (
  nodes: any[],
  projectId: string,
): Promise<any[]> => {
  const { loadProjectDisplayPropertyMappingFromIndexedDB } =
    await import('./indexeddb.helpers');
  const displayPropertyMapping =
    await loadProjectDisplayPropertyMappingFromIndexedDB(projectId);

  return nodes.map((node) => {
    const nodeLabel = node.labels?.[0];
    if (!nodeLabel) return node;

    // Get display property for this node label. Absent means "no explicit
    // choice", which is NOT the same as the reader picking 'label'.
    const displayProperty = displayPropertyMapping[nodeLabel];

    // Determine caption based on display property mapping
    let caption = nodeLabel; // Default fallback
    if (!displayProperty) {
      // Nothing configured: name the record, and fall back to its type only
      // when it carries nothing that names it.
      //
      // This used to caption every node with its bare type, which made the
      // same record look named or anonymous depending on how it was reached —
      // opening one from search showed "Australia, ATCO" because that path
      // seeds the node directly, while reloading the page replayed the
      // pipeline through here and relabelled it "Identity". A canvas that
      // already colours by type does not need every node to repeat it.
      caption =
        NODE_NAME_PROPERTIES.reduce<string>((found, key) => {
          if (found) return found;
          const value = node.properties?.[key] ?? node[key];
          return typeof value === 'string' && value ? value : '';
        }, '') || nodeLabel;
    } else if (displayProperty === 'label') {
      // An explicit choice of the type, which is honoured as given.
      caption = nodeLabel;
    } else {
      // Try to get the property value from node.properties or node itself
      const propertyValue =
        node.properties?.[displayProperty] ?? node[displayProperty];
      if (
        propertyValue !== undefined &&
        propertyValue !== null &&
        propertyValue !== ''
      ) {
        caption = String(propertyValue);
      } else {
        // Fallback to label if property doesn't exist or is empty
        caption = nodeLabel;
      }
    }

    return {
      ...node,
      caption,
    };
  });
};

/**
 * Checks if an array looks like a raw path: [node, relationshipType, node, relationshipType, ...]
 * where nodes are objects and relationship types are strings.
 */
function isPathLikeArray(value: any): value is any[] {
  if (!Array.isArray(value) || value.length < 2) {
    return false;
  }
  const first = value[0];
  const second = value[1];
  return (
    typeof first === 'object' &&
    first !== null &&
    typeof second === 'string' &&
    second.length > 0
  );
}

/**
 * Collects all path-like arrays from raw graph data.
 * Handles both a single record object (e.g. { path: [...], path2: [...] }) and an array of records.
 */
function collectPathArrays(data: any): any[][] {
  const pathArrays: any[][] = [];

  if (Array.isArray(data)) {
    data.forEach((row) => {
      if (row && typeof row === 'object') {
        Object.values(row).forEach((val) => {
          if (isPathLikeArray(val)) {
            pathArrays.push(val);
          }
        });
      }
    });
  } else if (data && typeof data === 'object') {
    Object.values(data).forEach((val) => {
      if (isPathLikeArray(val)) {
        pathArrays.push(val);
      }
    });
  }

  return pathArrays;
}

/**
 * Extracts nodes and relationships from raw path format.
 * Raw format: object with keys like "path", "path2" whose values are arrays
 * [node, relationshipType, node, relationshipType, ...] (alternating objects and strings).
 * Node objects typically have node_id or id; relationship types are strings (e.g. "HAS_WELL", "HAS_STAGE").
 *
 * @param data - Raw graph data (single record object or array of records with path-like arrays)
 * @returns { nodes, relationships } - Nodes have id/nodeId and labels; relationships have from, to, type
 */
export function extractNodesAndRelationshipsFromRawPaths(data: any): {
  nodes: any[];
  relationships: any[];
} {
  const pathArrays = collectPathArrays(data);
  if (pathArrays.length === 0) {
    return { nodes: [], relationships: [] };
  }

  const nodeMap = new Map<string, any>();
  const relationshipSet = new Set<string>();
  const relationships: any[] = [];
  let fallbackNodeCounter = 0;
  const objectToFallbackId = new WeakMap<object, string>();

  function getNodeId(node: any): string {
    const id = node?.node_id ?? node?.id;
    if (id !== undefined && id !== null && id !== '') {
      return String(id);
    }
    if (typeof node === 'object' && node !== null) {
      let fallback = objectToFallbackId.get(node);
      if (!fallback) {
        fallback = `node-${fallbackNodeCounter++}`;
        objectToFallbackId.set(node, fallback);
      }
      return fallback;
    }
    return `node-${fallbackNodeCounter++}`;
  }

  function ensureNode(node: any): string {
    const nodeId = getNodeId(node);
    if (!nodeMap.has(nodeId)) {
      nodeMap.set(nodeId, {
        ...node,
        id: nodeId,
        nodeId,
        labels: node.labels ?? ['Node'],
      });
    }
    return nodeId;
  }

  pathArrays.forEach((pathArray) => {
    for (let i = 0; i < pathArray.length; i += 2) {
      const node = pathArray[i];
      if (typeof node !== 'object' || node === null) {
        continue;
      }

      const nodeId = ensureNode(node);

      const relType = pathArray[i + 1];
      if (typeof relType === 'string' && i + 2 < pathArray.length) {
        const nextNode = pathArray[i + 2];
        if (typeof nextNode === 'object' && nextNode !== null) {
          const toId = ensureNode(nextNode);
          const relKey = `${nodeId}-${relType}-${toId}`;
          if (!relationshipSet.has(relKey)) {
            relationshipSet.add(relKey);
            relationships.push({
              id: relKey,
              from: nodeId,
              to: toId,
              type: relType,
              caption: relType,
            });
          }
        }
      }
    }
  });

  return {
    nodes: Array.from(nodeMap.values()),
    relationships,
  };
}

/**
 * Detects if the given data is in raw path format (object or array of objects
 * with path-like arrays: [node, relationshipType, node, ...]).
 */
export function isRawPathFormat(data: any): boolean {
  return collectPathArrays(data).length > 0;
}

/**
 * ISO 8601 datetime pattern (e.g. 2025-10-12T03:00:28.000000000+00:00 or 2025-10-12T03:00:28Z).
 * Matches date, 'T', time, optional fractional seconds, optional 'Z' or timezone offset.
 */
const ISO_DATETIME_PATTERN =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/i;

/**
 * ISO 8601 date-only pattern (e.g. 2022-12-31).
 * Matches YYYY-MM-DD. Neo4j datetime() accepts both for comparison.
 */
const ISO_DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Returns true if the value looks like an ISO 8601 date or datetime string
 * (e.g. 2022-12-31, 2025-10-12T03:00:28.000000000+00:00).
 * Used for filter/sort to apply datetime comparison instead of numeric.
 */
export function isIsoDateTimeString(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  const s = String(value).trim();
  if (s === '') return false;
  return ISO_DATE_ONLY_PATTERN.test(s) || ISO_DATETIME_PATTERN.test(s);
}

/** Safe characters for Cypher dynamic property key (no injection) */
function sanitizeFilterField(field: string | null | undefined): string | null {
  if (field == null || typeof field !== 'string') return null;
  const sanitized = field.replace(/[^a-zA-Z0-9_]/g, '');
  return sanitized.length > 0 ? sanitized : null;
}

export interface FilterConstraint {
  field: string;
  value: string;
  matchMode: string;
}

/**
 * Builds a Cypher WHERE clause and params from filter constraints (e.g. from DataTableFilterMeta).
 * All values are parameterized. Field names are sanitized (a-zA-Z0-9_ only).
 * Returns empty whereClause and params when constraints is empty or all have empty value.
 */
export function buildFilterWhereClause(constraints: FilterConstraint[]): {
  whereClause: string;
  params: Record<string, unknown>;
} {
  const parts: string[] = [];
  const params: Record<string, unknown> = {};
  let idx = 0;
  for (const c of constraints) {
    const value = c.value;
    if (value === undefined || value === null || String(value).trim() === '') {
      continue;
    }
    const field = sanitizeFilterField(c.field);
    if (!field) continue;
    const fieldKey = `filterField${idx}`;
    const valueKey = `filterValue${idx}`;
    params[fieldKey] = field;
    const mode = (c.matchMode || 'contains').toLowerCase();
    if (mode === 'contains') {
      params[valueKey] = String(value).trim();
      parts.push(
        `(toLower(toString(n[$${fieldKey}])) CONTAINS toLower($${valueKey}))`,
      );
    } else if (mode === 'notcontains') {
      params[valueKey] = String(value).trim();
      parts.push(
        `(NOT (toLower(toString(n[$${fieldKey}])) CONTAINS toLower($${valueKey})))`,
      );
    } else if (mode === 'equals') {
      const valueStr = String(value).trim();
      params[valueKey] = valueStr;
      // Use datetime comparison when value is ISO 8601 so Neo4j DateTime and string formats match
      if (isIsoDateTimeString(valueStr)) {
        parts.push(
          `(datetime(toString(n[$${fieldKey}])) = datetime($${valueKey}))`,
        );
      } else {
        parts.push(
          `(n[$${fieldKey}] = $${valueKey} OR toString(n[$${fieldKey}]) = $${valueKey})`,
        );
      }
    } else if (mode === 'notequals') {
      const valueStr = String(value).trim();
      params[valueKey] = valueStr;
      if (isIsoDateTimeString(valueStr)) {
        parts.push(
          `(datetime(toString(n[$${fieldKey}])) <> datetime($${valueKey}))`,
        );
      } else {
        parts.push(
          `(n[$${fieldKey}] <> $${valueKey} AND toString(n[$${fieldKey}]) <> $${valueKey})`,
        );
      }
    } else if (mode === 'startswith') {
      params[valueKey] = String(value).trim();
      parts.push(`(toString(n[$${fieldKey}]) STARTS WITH $${valueKey})`);
    } else if (mode === 'endswith') {
      params[valueKey] = String(value).trim();
      parts.push(`(toString(n[$${fieldKey}]) ENDS WITH $${valueKey})`);
    } else if (
      mode === 'lt' ||
      mode === 'lte' ||
      mode === 'gt' ||
      mode === 'gte'
    ) {
      const valueStr = String(value).trim();
      const op =
        mode === 'lt'
          ? '<'
          : mode === 'lte'
            ? '<='
            : mode === 'gt'
              ? '>'
              : '>=';
      if (isIsoDateTimeString(valueStr)) {
        params[valueKey] = valueStr;
        parts.push(
          `(datetime(toString(n[$${fieldKey}])) ${op} datetime($${valueKey}))`,
        );
      } else {
        const num = Number(value);
        if (Number.isNaN(num)) {
          params[valueKey] = valueStr;
        } else {
          params[valueKey] = num;
        }
        parts.push(`(toFloat(n[$${fieldKey}]) ${op} $${valueKey})`);
      }
    } else {
      params[valueKey] = String(value).trim();
      parts.push(
        `(toLower(toString(n[$${fieldKey}])) CONTAINS toLower($${valueKey}))`,
      );
    }
    idx += 1;
  }
  if (parts.length === 0) {
    return { whereClause: '', params: {} };
  }
  const whereClause = ' WHERE ' + parts.join(' AND ');
  return { whereClause, params };
}
